import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { CreditCard, Smartphone, Shield, Users, Target, TrendingUp, Award, ArrowRight, Building, Phone, DollarSign, CheckCircle, Globe, Lock } from "lucide-react";

function FundWork() {
  const [isVisible, setIsVisible] = useState({});
  const [activeTab, setActiveTab] = useState('banking');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, []);

  const fundingStats = [
    { 
      icon: Shield, 
      number: "100%", 
      label: "Transparency", 
      description: "All funds subject to internal & external audits",
      color: "from-blue-500 to-cyan-500" 
    },
    { 
      icon: CheckCircle, 
      number: "Direct", 
      label: "Impact", 
      description: "Funds go straight to community projects",
      color: "from-green-500 to-emerald-500" 
    },
    { 
      icon: Globe, 
      number: "Multiple", 
      label: "Payment Options", 
      description: "Bank transfers and mobile money accepted",
      color: "from-purple-500 to-violet-500" 
    },
    { 
      icon: Award, 
      number: "Trusted", 
      label: "Organization", 
      description: "Registered and audited community organization",
      color: "from-orange-500 to-amber-500" 
    }
  ];

  const paymentMethods = [
    {
      id: 'banking',
      icon: Building,
      title: 'Bank Transfer',
      description: 'Secure bank transfers for larger contributions',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'mobile',
      icon: Smartphone,
      title: 'Mobile Money',
      description: 'Quick and convenient mobile money transfers',
      color: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-40 pb-15">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-indigo-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
          
          {/* Money/Security pattern */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-5">
            <Shield className="w-full h-full text-blue-600" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div 
            id="hero-content"
            data-animate
            className={`text-center transform transition-all duration-1000 ${
              isVisible['hero-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <DollarSign className="w-4 h-4 mr-2" />
              Secure Funding Platform
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Fund Our
              </span>
              <br />
              <span className="text-gray-800">Work</span>
            </h1>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-r-2xl mb-8 max-w-4xl mx-auto">
              <p className="text-lg text-gray-700 font-medium">
                <span className="font-bold text-blue-800">Your investment</span> in our community creates lasting change. 
                Every contribution is carefully managed, transparently reported, and directly channeled into 
                programs that transform lives in Kayunga.
              </p>
            </div>
            
            <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              All grants and donations are processed through our registered organization bank account 
              and mobile money systems, ensuring complete transparency and accountability through 
              comprehensive internal and external auditing processes.
            </p>
          </div>
        </div>
      </div>

     {/* Payment Methods Section */}
     <div className="py-5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              How to <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Contribute</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose your preferred method to support our community initiatives
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-12">
            {paymentMethods.map((method) => {
              const Icon = method.icon;
              return (
                <button
                  key={method.id}
                  onClick={() => setActiveTab(method.id)}
                  className={`flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 mx-2 ${
                    activeTab === method.id 
                      ? `bg-gradient-to-r ${method.color} text-white shadow-lg transform scale-105` 
                      : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  {method.title}
                </button>
              );
            })}
          </div>

          {/* Payment Content */}
          <div className="transition-all duration-500">
            {activeTab === 'banking' && (
              <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Building className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">Bank Transfer Details</h3>
                  <p className="text-lg text-gray-600">Secure and reliable bank transfers for all contribution amounts</p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8">
                  <div className="overflow-x-auto">
                    <table className="w-full text-lg">
                      <thead>
                        <tr className="border-b-2 border-blue-200">
                          <th className="text-left py-4 px-6 font-bold text-blue-800">Bank</th>
                          <th className="text-left py-4 px-6 font-bold text-blue-800">Account Name</th>
                          <th className="text-left py-4 px-6 font-bold text-blue-800">Account Number</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="hover:bg-blue-100/50 transition-colors">
                          <td className="py-6 px-6 font-semibold text-gray-800">Finance Trust Bank</td>
                          <td className="py-6 px-6 font-semibold text-gray-800">Kayunga Community Club</td>
                          <td className="py-6 px-6 font-semibold text-blue-600 text-xl">204214000523</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-6 rounded-r-2xl">
                  <div className="flex items-start gap-3">
                    <Lock className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-amber-800 mb-2">Security & Transparency</h4>
                      <p className="text-amber-700">
                        All project funds are subjected to rigorous internal and external audits. 
                        We provide detailed financial reports to ensure complete transparency in how your contributions are utilized.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'mobile' && (
              <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">Mobile Money Transfer</h3>
                  <p className="text-lg text-gray-600">Quick and convenient mobile money contributions</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-l-4 border-green-500">
                    <div className="flex items-center gap-3 mb-4">
                      <Phone className="w-8 h-8 text-green-600" />
                      <h4 className="text-xl font-bold text-green-800">Primary Contact</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="text-3xl font-black text-green-700">0773041905</div>
                      <div className="text-lg font-semibold text-gray-700">Kantono Ritah</div>
                      <div className="text-sm text-gray-600">Organization Representative</div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-l-4 border-blue-500">
                    <div className="flex items-center gap-3 mb-4">
                      <Phone className="w-8 h-8 text-blue-600" />
                      <h4 className="text-xl font-bold text-blue-800">Alternative Contact</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="text-3xl font-black text-blue-700">0757419919</div>
                      <div className="text-lg font-semibold text-gray-700">Kantono Ritah</div>
                      <div className="text-sm text-gray-600">Secondary Line</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6" />
                    Mobile Money Instructions
                  </h4>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">1</div>
                      <div>
                        <p className="font-semibold">Send contributions to either mobile number above</p>
                        <p className="text-sm text-gray-600">Both numbers are registered under Kantono Ritah</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">2</div>
                      <div>
                        <p className="font-semibold">Use "Kantono Ritah" as the recipient name</p>
                        <p className="text-sm text-gray-600">Ensure name matches exactly for successful transfer</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">3</div>
                      <div>
                        <p className="font-semibold">Include your purpose in the transaction note</p>
                        <p className="text-sm text-gray-600">Example: "Community Project Donation"</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      

       {/* Trust & Transparency Section */}
       <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="stats-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['stats-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Built on <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Trust</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your confidence in our mission drives our commitment to complete transparency and accountability
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {fundingStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  id={`stat-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`stat-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-blue-500">
                    <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-gray-800 mb-2">{stat.number}</div>
                      <div className="text-lg font-semibold text-blue-600 mb-2">{stat.label}</div>
                      <div className="text-sm text-gray-600">{stat.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <Shield className="w-5 h-5 mr-3" />
              Secure • Transparent • Impactful
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Invest in Community Change
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Your contribution creates ripple effects of positive change throughout Kayunga. 
              Every donation is an investment in sustainable community development and human dignity.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button 
                onClick={() => window.location.href = '/contact-us'}
                className="bg-white text-blue-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                <Users className="w-5 h-5" />
                Partner With Us
              </button>
            </div>
            
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-white/80 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="text-2xl font-bold mb-2">Bank Transfer</div>
                <div className="text-sm">Secure for larger contributions</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="text-2xl font-bold mb-2">Mobile Money</div>
                <div className="text-sm">Quick & convenient transfers</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="text-2xl font-bold mb-2">Full Transparency</div>
                <div className="text-sm">Complete audit trail</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default FundWork;